import React, { ReactNode } from "react";
import clsx from "clsx";

import { TEACHER_SESSION_KEY } from "../../../Definition";
import lessonWorkFlow from "../../../assets/images/lesson_work_flow.png";
import { ReactComponent as FlowArrow } from "../../../assets/svg/flow_arrow.svg";
import { ReactComponent as VideoIcon } from "../../../assets/svg/select_lesson_video.svg";
import { ReactComponent as EnpitsuIcon } from "../../../assets/svg/enpitsu-02.svg";
import Modal from "../../../atoms/Modal";
import Button from "../../../atoms/Button";
import StepTitle from "../../../molecules/teacher/StepTitle";

import styles from "./VideoLessonGuideModal.module.scss";

interface ChapterDiagramProps {
  title: string;
}

const ChapterDiagram = ({ title }: ChapterDiagramProps) => {
  return (
    <div className={styles.lessonFlow__diagram__step}>
      <p>{title}</p>
      <div className={styles.chapterContainer}>
        <VideoIcon />
      </div>
    </div>
  );
};

interface WorkDiagramProps {
  title: string;
  issue: ReactNode;
}

const WorkDiagram = ({ title, issue }: WorkDiagramProps) => {
  return (
    <div className={clsx(styles.lessonFlow__diagram__step, styles.teacherNote)}>
      <p>{title}</p>
      <div className={styles.workContainer}>
        <p>{issue}</p>
        <div className={styles.workContainer__inputField}>
          <EnpitsuIcon />
        </div>
      </div>
    </div>
  );
};

interface VideoLessonGuideModalProps {
  onClose: () => void;
}

export const VideoLessonGuideModal = ({
  onClose,
}: VideoLessonGuideModalProps) => {
  const sessionId = sessionStorage.getItem(TEACHER_SESSION_KEY);
  const extra = sessionId ? `&session_id=${sessionId}` : "";

  return (
    <Modal className={styles.contents} centered onClose={onClose} hasCloseMark>
      <div className={styles.contentOne}>
        <StepTitle
          title="ナビ動画とは？"
          currentStep={1}
          totalStep={3}
          titleStepGap
        />
        <p className={styles.supplement}>
          ENAGEEDのナビ動画は、説明を動画に任せることで、
          <br />
          先生が生徒と対話し、考えを深める時間を最大化するための教材です。
        </p>
        <p>
          知識のインプットは「動画」、
          <span className={styles.teacherNote}>
            対話やワークの進行は「先生」
          </span>
          <span className={styles.supplement}>が担います。</span>
        </p>
        <div className={styles.lessonFlow}>
          <p>1Lessonの流れ(例)</p>
          <div className={styles.lessonFlow__diagram}>
            <ChapterDiagram title="チャプター01" />
            <FlowArrow className={styles.flowArrow} />
            <WorkDiagram
              title="WORK01"
              issue={
                <>
                  かっこいい大人って
                  <br />
                  どんな大人？
                </>
              }
            />
            <FlowArrow className={styles.flowArrow} />
            <ChapterDiagram title="チャプター02" />
            <FlowArrow className={styles.flowArrow} />
            <WorkDiagram
              title="WORK02"
              issue={
                <>
                  AI・ロボットに
                  <br />
                  負けない力って？
                </>
              }
            />
            <FlowArrow className={styles.flowArrow} />
            <ChapterDiagram title="チャプター03" />
            <FlowArrow className={styles.flowArrow} />
            <WorkDiagram
              title="振り返り"
              issue={
                <>
                  今日の学びを
                  <br />
                  振り返ろう
                </>
              }
            />
          </div>
        </div>
      </div>
      <div className={styles.contentTwo}>
        <StepTitle
          title="ナビ動画での授業方法"
          currentStep={2}
          totalStep={3}
          titleStepGap
        />
        <ol>
          <li>チャプター01の「再生する」をクリックします。</li>
          <li>
            動画の終盤でワークの案内が表示されたら、動画は自動で停止します。
            <br />
            そのまま
            <span className={styles.teacherNote}>
              先生が進行役となり、ワークを実施
            </span>
            してください。
          </li>
          <li>
            ワークが終了したら、「次のチャプター」を再生します。
            <br />
            <span className={styles.supplement}>
              ※以降も「動画 → ワーク」を順に繰り返して授業を進めます。
            </span>
          </li>
          <li>
            最後のチャプターの動画が終了したら、「授業を終了する」をクリックし、振り返りを保存してください。
          </li>
        </ol>
      </div>
      <div className={styles.contentThree}>
        <StepTitle
          title="ワークの進行方法（先生）"
          currentStep={3}
          totalStep={3}
          highlighted
        />
        <div className={styles.lessonWorkFlow}>
          <div>
            <img
              src={lessonWorkFlow}
              alt="ワーク進行フロー"
              width={200}
              height={212}
            />
            <a
              className={styles.lessonWorkFlow__reference}
              href={`${process.env.REACT_APP_ONE_MENU_URL}/contents/support_tools?tab=second_tab${extra}#knowhow_for_lesson`}
              target="_blank"
              rel="noreferrer"
            >
              参考：授業のポイント動画
            </a>
          </div>
          <ol>
            <li>
              生徒の考えを引き出す
              <br />
              <span className={styles.supplement}>
                先生は答えを教えるのではなく、問いかけや例の提示を通して、生徒が考え始めるきっかけをつくります。
                <br />
                <span className={styles.concreteExample}>
                  「身近なところから考えるのはどう？」「先生が学生の頃だったらこう考えるかも。」「他校の生徒はこんなふうに答えてるみたいだよ！」
                </span>
              </span>
            </li>
            <li>
              どんな考えもまず認める <br />
              <span className={styles.supplement}>
                どんな答えであっても、まずは否定せずに受け止めます。意見の違いを価値として扱い、生徒同士が互いの考えを認め合える場をつくります。
                <br />
                <span className={styles.concreteExample}>
                  「面白い切り口だね！」「深く考えられてるね〜！」
                </span>
              </span>
            </li>
            <li>
              考えのプロセスに目を向ける
              <br />
              <span className={styles.supplement}>
                意見が出たら、「なぜそう思ったのか」「どんな経験があったのか」を丁寧に問い返します。結論ではなく、そこに至る過程に着目し、生徒の考えの真意を引き出していきます。
                <br />
                <span className={styles.concreteExample}>
                  「どうしてそう思ったの？」「そう思ったきっかけはある？」
                </span>
              </span>
            </li>
          </ol>
        </div>
      </div>
      <Button
        name="閉じる"
        variant="normal"
        color="gray"
        className={styles.buttonLayout}
        onClick={onClose}
      />
    </Modal>
  );
};

export default VideoLessonGuideModal;
